import { useEffect, useState } from 'react';
import adminApi from '@/lib/adminApi';

const AdminAiStudio = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingToolId, setSavingToolId] = useState('');

  const loadAiStudio = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminApi.get('/admin/ai-studio');
      setData(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to load AI Studio usage.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAiStudio();
  }, []);

  const updateSetting = (field, value) => {
    setData((current) => ({
      ...current,
      settings: {
        ...(current?.settings || {}),
        [field]: value,
      },
    }));
  };

  const updateTool = (toolId, field, value) => {
    setData((current) => ({
      ...current,
      tools: (current?.tools || []).map((tool) => (
        tool.id === toolId ? { ...tool, [field]: value } : tool
      )),
    }));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setStatus('');
    setError('');
    try {
      const response = await adminApi.put('/admin/ai-studio/settings', settings);
      setData((current) => ({ ...current, settings: response.data.settings }));
      setStatus('AI Studio settings saved.');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save AI Studio settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const settings = data?.settings || {};
  const tools = data?.tools || [];
  const platform = data?.platform || {};
  const users = data?.users || [];
  const moderationEvents = data?.moderation_events || [];

  const saveTool = async (tool) => {
    setSavingToolId(tool.id);
    setStatus('');
    setError('');
    try {
      const response = await adminApi.put(`/admin/ai-studio/tools/${tool.id}`, {
        enabled: Boolean(tool.enabled),
        access_tier: tool.access_tier || 'free_beta',
        credit_cost: Number(tool.credit_cost || 0),
      });
      setData((current) => ({
        ...current,
        tools: (current?.tools || []).map((item) => (item.id === tool.id ? response.data.tool : item)),
      }));
      setStatus(`${tool.title} controls saved.`);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save AI Studio tool controls.');
    } finally {
      setSavingToolId('');
    }
  };

  const updateUserRestriction = async (user, restricted) => {
    setStatus('');
    setError('');
    try {
      await adminApi.post(`/admin/ai-studio/users/${user.role}/${user.user_id}/restriction`, {
        restricted,
        reason: restricted ? 'Restricted by admin from AI Studio dashboard' : '',
      });
      setStatus(restricted ? 'User restricted from AI Studio.' : 'User restored for AI Studio.');
      loadAiStudio();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to update AI Studio user restriction.');
    }
  };

  const restrictionByUserKey = users.reduce((map, user) => {
    map[`${user.role}-${user.user_id}`] = user;
    return map;
  }, {});

  const updateModerationUserRestriction = async (event, restricted) => {
    setStatus('');
    setError('');
    try {
      await adminApi.post(`/admin/ai-studio/users/${event.user_role}/${event.user_id}/restriction`, {
        restricted,
        reason: restricted ? `Restricted from AI safety alert: ${(event.categories || []).join(', ') || event.tool || 'AI Studio'}` : '',
      });
      setStatus(restricted ? 'Flagged user restricted from AI Studio.' : 'Flagged user restored for AI Studio.');
      loadAiStudio();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to update flagged user restriction.');
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#4A148C]">AI Studio</p>
            <h2 className="mt-2 text-3xl font-bold text-gray-900">Usage, credits, and launch limits</h2>
            <p className="mt-3 max-w-3xl text-gray-600">
              Monitor who is using AI Studio and how much of the free launch pool is being consumed. Paid subscription controls can build on this same usage data later.
            </p>
          </div>
          <button type="button" onClick={loadAiStudio} className="btn-outline">
            Refresh
          </button>
        </div>
      </section>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {status && <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{status}</div>}
      {loading && <div className="text-gray-500">Loading AI Studio usage...</div>}

      {!loading && data && (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-[1.75rem] border border-purple-100 bg-white p-5 shadow-lg">
              <p className="text-sm text-gray-500">AI Studio enabled</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{settings.enabled ? 'Yes' : 'No'}</p>
            </div>
            <div className="rounded-[1.75rem] border border-purple-100 bg-white p-5 shadow-lg">
              <p className="text-sm text-gray-500">Platform used today</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{platform.daily_used || 0} / {settings.global_daily_limit}</p>
            </div>
            <div className="rounded-[1.75rem] border border-purple-100 bg-white p-5 shadow-lg">
              <p className="text-sm text-gray-500">Platform used this month</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{platform.monthly_used || 0} / {settings.global_monthly_limit}</p>
            </div>
            <div className="rounded-[1.75rem] border border-purple-100 bg-white p-5 shadow-lg">
              <p className="text-sm text-gray-500">Per-user free limit</p>
              <p className="mt-2 text-2xl font-bold text-gray-900">{settings.free_daily_limit}/day</p>
              <p className="mt-1 text-sm text-gray-500">{settings.free_monthly_limit}/month</p>
            </div>
          </section>

          <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Admin controls</h3>
                <p className="mt-2 text-sm leading-6 text-gray-500">
                  These settings control free beta usage before paid subscriptions are launched.
                </p>
              </div>
              <button type="button" onClick={saveSettings} disabled={savingSettings} className="btn-primary disabled:bg-gray-300">
                {savingSettings ? 'Saving...' : 'Save controls'}
              </button>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <label className="flex items-center gap-3 rounded-2xl border border-orange-100 p-4">
                <input
                  type="checkbox"
                  checked={Boolean(settings.enabled)}
                  onChange={(event) => updateSetting('enabled', event.target.checked)}
                  className="h-5 w-5"
                />
                <span>
                  <span className="block text-sm font-bold text-gray-900">AI Studio enabled</span>
                  <span className="block text-xs text-gray-500">Turn off globally if usage or safety needs review.</span>
                </span>
              </label>
              {[
                ['free_daily_limit', 'Per-user daily limit'],
                ['free_monthly_limit', 'Per-user monthly limit'],
                ['global_daily_limit', 'Platform daily pool'],
                ['global_monthly_limit', 'Platform monthly pool'],
                ['max_details_chars', 'Prompt character limit'],
              ].map(([field, label]) => (
                <label key={field} className="block">
                  <span className="text-sm font-bold text-gray-800">{label}</span>
                  <input
                    type="number"
                    min="0"
                    value={settings[field] ?? 0}
                    onChange={(event) => updateSetting(field, Number(event.target.value))}
                    className="mt-2 w-full rounded-2xl border border-orange-100 px-4 py-3"
                  />
                </label>
              ))}
              <label className="flex items-center gap-3 rounded-2xl border border-orange-100 p-4">
                <input
                  type="checkbox"
                  checked={Boolean(settings.moderation_enabled)}
                  onChange={(event) => updateSetting('moderation_enabled', event.target.checked)}
                  className="h-5 w-5"
                />
                <span>
                  <span className="block text-sm font-bold text-gray-900">Moderate AI prompts</span>
                  <span className="block text-xs text-gray-500">Block abusive, adult, hateful, or unsafe AI requests.</span>
                </span>
              </label>
              <label className="flex items-center gap-3 rounded-2xl border border-orange-100 p-4">
                <input
                  type="checkbox"
                  checked={Boolean(settings.auto_restrict_flagged_users)}
                  onChange={(event) => updateSetting('auto_restrict_flagged_users', event.target.checked)}
                  className="h-5 w-5"
                />
                <span>
                  <span className="block text-sm font-bold text-gray-900">Auto restrict flagged users</span>
                  <span className="block text-xs text-gray-500">Unsafe AI use blocks the account from AI Studio.</span>
                </span>
              </label>
            </div>

            <label className="mt-5 block">
              <span className="text-sm font-bold text-gray-800">AI safety policy notice</span>
              <textarea
                rows="3"
                value={settings.policy_notice || ''}
                onChange={(event) => updateSetting('policy_notice', event.target.value)}
                className="mt-2 w-full rounded-2xl border border-orange-100 px-4 py-3 text-sm"
              />
            </label>
          </section>

          <section className="rounded-[2rem] border border-emerald-100 bg-white shadow-lg">
            <div className="border-b border-emerald-100 p-5 sm:p-6">
              <h3 className="text-2xl font-bold text-gray-900">Per-tool controls</h3>
              <p className="mt-2 text-sm leading-6 text-gray-500">
                Enable or pause individual AI Studio tools for website and Android app. Credit cost is stored now so paid plans can use the same controls later.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-emerald-100 text-sm">
                <thead className="bg-emerald-50/70 text-left text-xs font-bold uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="px-5 py-3">Tool</th>
                    <th className="px-5 py-3">Category</th>
                    <th className="px-5 py-3">Enabled</th>
                    <th className="px-5 py-3">Access</th>
                    <th className="px-5 py-3">Credits</th>
                    <th className="px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-50">
                  {tools.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-5 py-6 text-gray-500">No AI Studio tools found.</td>
                    </tr>
                  )}
                  {tools.map((tool) => (
                    <tr key={tool.id} className="align-middle">
                      <td className="px-5 py-4">
                        <p className="font-bold text-gray-900">{tool.title}</p>
                        <p className="mt-1 text-xs text-gray-500">{tool.id}</p>
                      </td>
                      <td className="px-5 py-4 text-gray-700">{tool.category}</td>
                      <td className="px-5 py-4">
                        <label className="inline-flex items-center gap-2 font-bold text-gray-700">
                          <input
                            type="checkbox"
                            checked={Boolean(tool.enabled)}
                            onChange={(event) => updateTool(tool.id, 'enabled', event.target.checked)}
                            className="h-5 w-5"
                          />
                          {tool.enabled ? 'On' : 'Off'}
                        </label>
                      </td>
                      <td className="px-5 py-4">
                        <select
                          value={tool.access_tier || 'free_beta'}
                          onChange={(event) => updateTool(tool.id, 'access_tier', event.target.value)}
                          className="w-40 rounded-xl border border-emerald-100 px-3 py-2"
                        >
                          <option value="free_beta">Free beta</option>
                          <option value="free">Free</option>
                          <option value="premium">Premium later</option>
                          <option value="admin_only">Admin only</option>
                        </select>
                      </td>
                      <td className="px-5 py-4">
                        <input
                          type="number"
                          min="1"
                          value={tool.credit_cost ?? 1}
                          onChange={(event) => updateTool(tool.id, 'credit_cost', Number(event.target.value))}
                          className="w-24 rounded-xl border border-emerald-100 px-3 py-2"
                        />
                      </td>
                      <td className="px-5 py-4">
                        <button
                          type="button"
                          onClick={() => saveTool(tool)}
                          disabled={savingToolId === tool.id}
                          className="rounded-full bg-emerald-700 px-4 py-2 text-sm font-bold text-white transition hover:bg-emerald-800 disabled:bg-gray-300"
                        >
                          {savingToolId === tool.id ? 'Saving...' : 'Save'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-[2rem] border border-orange-100 bg-white shadow-lg">
            <div className="border-b border-orange-100 p-5 sm:p-6">
              <h3 className="text-2xl font-bold text-gray-900">User usage</h3>
              <p className="mt-2 text-sm text-gray-500">Successful generations only. Failed OpenAI calls are not counted.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-orange-100 text-sm">
                <thead className="bg-orange-50/70 text-left text-xs font-bold uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="px-5 py-3">User</th>
                    <th className="px-5 py-3">Role</th>
                    <th className="px-5 py-3">Today</th>
                    <th className="px-5 py-3">Month</th>
                    <th className="px-5 py-3">Restriction</th>
                    <th className="px-5 py-3">Last used</th>
                    <th className="px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-orange-50">
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-5 py-6 text-gray-500">No AI Studio usage recorded yet.</td>
                    </tr>
                  )}
                  {users.map((user) => (
                    <tr key={`${user.role}-${user.user_id}`} className="align-top">
                      <td className="px-5 py-4">
                        <p className="font-semibold text-gray-900">{user.name}</p>
                        <p className="mt-1 break-all text-xs text-gray-500">{user.email || user.user_id}</p>
                      </td>
                      <td className="px-5 py-4 capitalize text-gray-700">{user.role}</td>
                      <td className="px-5 py-4 font-semibold text-gray-900">{user.daily_used}</td>
                      <td className="px-5 py-4 font-semibold text-gray-900">{user.monthly_used}</td>
                      <td className="px-5 py-4">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ${user.restricted ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {user.restricted ? 'Restricted' : 'Allowed'}
                        </span>
                        {user.restriction_reason && <p className="mt-1 text-xs text-gray-500">{user.restriction_reason}</p>}
                      </td>
                      <td className="px-5 py-4 text-gray-600">{user.last_used_at || 'NA'}</td>
                      <td className="px-5 py-4">
                        <button
                          type="button"
                          onClick={() => updateUserRestriction(user, !user.restricted)}
                          className={user.restricted ? 'btn-outline' : 'rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50'}
                        >
                          {user.restricted ? 'Allow' : 'Restrict'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-[2rem] border border-red-100 bg-white shadow-lg">
            <div className="border-b border-red-100 p-5 sm:p-6">
              <h3 className="text-2xl font-bold text-gray-900">AI safety alerts</h3>
              <p className="mt-2 text-sm text-gray-500">Blocked abusive/adult/unsafe AI Studio attempts from website and Android app.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-red-100 text-sm">
                <thead className="bg-red-50/70 text-left text-xs font-bold uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="px-5 py-3">User</th>
                    <th className="px-5 py-3">Tool</th>
                    <th className="px-5 py-3">Categories</th>
                    <th className="px-5 py-3">Source</th>
                    <th className="px-5 py-3">Prompt</th>
                    <th className="px-5 py-3">Time</th>
                    <th className="px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-50">
                  {moderationEvents.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-5 py-6 text-gray-500">No AI safety alerts recorded yet.</td>
                    </tr>
                  )}
                  {moderationEvents.map((event) => {
                    const user = restrictionByUserKey[`${event.user_role}-${event.user_id}`] || {};
                    const restricted = Boolean(user.restricted);
                    return (
                    <tr key={event.id} className="align-top">
                      <td className="px-5 py-4">
                        <p className="font-semibold text-gray-900">{event.user_name || user.name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{event.user_role} · {event.user_id}</p>
                        {restricted && <p className="mt-1 text-xs font-bold text-red-600">Currently restricted</p>}
                      </td>
                      <td className="px-5 py-4">{event.tool}</td>
                      <td className="px-5 py-4">{(event.categories || []).join(', ') || 'Flagged'}</td>
                      <td className="px-5 py-4">{event.source}</td>
                      <td className="max-w-md px-5 py-4 text-gray-600">{event.prompt_text}</td>
                      <td className="px-5 py-4 text-gray-600">{event.created_at || 'NA'}</td>
                      <td className="px-5 py-4">
                        <button
                          type="button"
                          onClick={() => updateModerationUserRestriction(event, !restricted)}
                          className={restricted ? 'btn-outline' : 'rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50'}
                        >
                          {restricted ? 'Allow user' : 'Restrict user'}
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-[2rem] border border-purple-100 bg-purple-50/60 p-6">
            <h3 className="text-xl font-bold text-gray-900">Next pricing stage</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              Later we can add plan setup, credit packs, Razorpay subscription mapping, and manual credit adjustments here. For now, this page keeps free launch usage visible and controlled.
            </p>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminAiStudio;
